import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-production-6edb.up.railway.app';

export default function SplitTiers() {
  const { profile } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [tiers, setTiers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [applyNotes, setApplyNotes] = useState('');
  const [applyingTierId, setApplyingTierId] = useState(null);

  const [newTier, setNewTier] = useState({
    tier_name: '',
    tier_description: '',
    hma_percentage: '25',
    hma_company_percentage: '25',
    mentor_percentage: '0'
  });

  // Fetch coaches on mount
  useEffect(() => {
    fetchCoaches();
  }, []);

  // Fetch tiers + history when a coach is selected
  useEffect(() => {
    if (selectedCoachId) {
      fetchTiersAndHistory(selectedCoachId);
    } else {
      setTiers([]);
      setHistory([]);
    }
  }, [selectedCoachId]);

  const fetchCoaches = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, role')
      .in('role', ['coach', 'senior_coach'])
      .order('full_name');
    if (!error) setCoaches(data || []);
  };

  const fetchTiersAndHistory = async (coachId) => {
    setLoading(true);
    setError('');
    const [tiersRes, histRes] = await Promise.all([
      supabase
        .from('split_tiers')
        .select('*')
        .eq('coach_id', coachId)
        .order('created_at', { ascending: false }),
      supabase
        .from('split_tier_history')
        .select('*, applied_by_profile:applied_by(full_name, email)')
        .eq('coach_id', coachId)
        .order('applied_at', { ascending: false })
        .limit(20)
    ]);
    if (tiersRes.error) setError(tiersRes.error.message);
    else setTiers(tiersRes.data || []);
    if (!histRes.error) setHistory(histRes.data || []);
    setLoading(false);
  };

  const handleAddTier = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const hma = parseFloat(newTier.hma_percentage);
      const hmaCompany = parseFloat(newTier.hma_company_percentage);
      const mentor = parseFloat(newTier.mentor_percentage);

      if (Math.abs((hma - hmaCompany - mentor)) > 0.01) {
        setError('hma_percentage must equal hma_company_percentage + mentor_percentage');
        setSaving(false);
        return;
      }

      const { error: insertErr } = await supabase
        .from('split_tiers')
        .insert({
          coach_id: selectedCoachId,
          tier_name: newTier.tier_name.trim(),
          tier_description: newTier.tier_description.trim() || null,
          hma_percentage: hma,
          hma_company_percentage: hmaCompany,
          mentor_percentage: mentor,
          is_active: false
        });

      if (insertErr) throw insertErr;
      setSuccess('Tier created successfully.');
      setShowAddForm(false);
      setNewTier({ tier_name: '', tier_description: '', hma_percentage: '25', hma_company_percentage: '25', mentor_percentage: '0' });
      fetchTiersAndHistory(selectedCoachId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTier = async (tierId) => {
    setError('');
    setApplying(true);
    setApplyingTierId(tierId);
    try {
      const { error: rpcErr } = await supabase.rpc('apply_split_tier', {
        p_tier_id: tierId,
        p_admin_id: profile.id,
        p_notes: applyNotes.trim() || null
      });
      if (rpcErr) throw rpcErr;
      setSuccess('Tier applied successfully. The coach\'s split percentages have been updated.');
      setApplyNotes('');
      setApplyingTierId(null);
      fetchTiersAndHistory(selectedCoachId);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const handleDeleteTier = async (tierId, isActive) => {
    if (isActive) {
      setError('Cannot delete the currently active tier. Apply another tier first.');
      return;
    }
    if (!window.confirm('Delete this tier? This cannot be undone.')) return;
    const { error: delErr } = await supabase
      .from('split_tiers')
      .delete()
      .eq('id', tierId);
    if (delErr) setError(delErr.message);
    else {
      setSuccess('Tier deleted.');
      fetchTiersAndHistory(selectedCoachId);
    }
  };

  const selectedCoach = coaches.find(c => c.id === selectedCoachId);

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Split Tiers</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Manage revenue split tiers per coach. Apply a tier to update their HMA percentage immediately.
        </p>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>
      )}
      {success && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>
      )}

      {/* Coach Selector */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Select a Coach</h3>
        <select
          className="input"
          style={{ maxWidth: 400 }}
          value={selectedCoachId}
          onChange={e => { setSelectedCoachId(e.target.value); setError(''); setSuccess(''); }}
        >
          <option value="">— Choose a coach —</option>
          {coaches.map(c => (
            <option key={c.id} value={c.id}>
              {c.full_name || c.email} ({c.role === 'senior_coach' ? 'Senior Coach' : 'Coach'})
            </option>
          ))}
        </select>
      </div>

      {selectedCoachId && (
        <>
          {/* Current Split Summary */}
          {tiers.length > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600 }}>
                  Tiers for {selectedCoach?.full_name || selectedCoach?.email}
                </h3>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => { setShowAddForm(v => !v); setError(''); setSuccess(''); }}
                >
                  {showAddForm ? 'Cancel' : '+ Add Tier'}
                </button>
              </div>

              {loading ? (
                <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>Loading tiers...</div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {tiers.map(tier => (
                    <div key={tier.id} style={{
                      border: `1px solid ${tier.is_active ? 'var(--success)' : 'var(--border)'}`,
                      borderRadius: 10,
                      padding: 16,
                      background: tier.is_active ? 'rgba(76,175,130,0.06)' : 'var(--bg-secondary)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, fontSize: 15 }}>{tier.tier_name}</span>
                            {tier.is_active && (
                              <span style={{
                                background: 'rgba(76,175,130,0.15)', color: 'var(--success)',
                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                textTransform: 'uppercase', letterSpacing: '0.05em'
                              }}>Active</span>
                            )}
                          </div>
                          {tier.tier_description && (
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                              {tier.tier_description}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 20, fontSize: 13 }}>
                            <span>
                              <span style={{ color: 'var(--text-muted)' }}>HMA total: </span>
                              <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{tier.hma_percentage}%</span>
                            </span>
                            <span>
                              <span style={{ color: 'var(--text-muted)' }}>HMA company: </span>
                              <span style={{ fontWeight: 600 }}>{tier.hma_company_percentage}%</span>
                            </span>
                            <span>
                              <span style={{ color: 'var(--text-muted)' }}>Mentor: </span>
                              <span style={{ fontWeight: 600 }}>{tier.mentor_percentage}%</span>
                            </span>
                            <span>
                              <span style={{ color: 'var(--text-muted)' }}>Coach keeps: </span>
                              <span style={{ fontWeight: 600, color: 'var(--success)' }}>
                                {(100 - tier.hma_percentage).toFixed(2)}%
                              </span>
                            </span>
                          </div>
                          {tier.applied_at && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                              Last applied: {new Date(tier.applied_at).toLocaleDateString('en-CA')}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                          {!tier.is_active && (
                            <>
                              <button
                                className="btn btn-primary btn-sm"
                                disabled={applying}
                                onClick={() => handleApplyTier(tier.id)}
                              >
                                {applying && applyingTierId === tier.id ? 'Applying...' : 'Apply'}
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteTier(tier.id, tier.is_active)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Optional notes before applying */}
              {tiers.some(t => !t.is_active) && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <label className="label" style={{ marginBottom: 6 }}>Notes for tier change (optional)</label>
                  <input
                    className="input"
                    style={{ maxWidth: 400 }}
                    placeholder="e.g. Revenue milestone reached"
                    value={applyNotes}
                    onChange={e => setApplyNotes(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Add Tier Form (or shown when no tiers exist) */}
          {(showAddForm || tiers.length === 0) && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
                {tiers.length === 0 ? 'Create First Tier' : 'Add New Tier'}
              </h3>
              <form onSubmit={handleAddTier} style={{ maxWidth: 480 }}>
                <div className="form-group">
                  <label className="label">Tier Name *</label>
                  <input
                    className="input"
                    required
                    placeholder="e.g. Starter, Growing, Established"
                    value={newTier.tier_name}
                    onChange={e => setNewTier(f => ({ ...f, tier_name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Description</label>
                  <input
                    className="input"
                    placeholder="e.g. 0–2k/month revenue"
                    value={newTier.tier_description}
                    onChange={e => setNewTier(f => ({ ...f, tier_description: e.target.value }))}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="label">HMA Total %</label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      value={newTier.hma_percentage}
                      onChange={e => setNewTier(f => ({ ...f, hma_percentage: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">HMA Company %</label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      value={newTier.hma_company_percentage}
                      onChange={e => setNewTier(f => ({ ...f, hma_company_percentage: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Mentor %</label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      value={newTier.mentor_percentage}
                      onChange={e => setNewTier(f => ({ ...f, mentor_percentage: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                  HMA Total must equal HMA Company + Mentor. Coach keeps {
                    isNaN(parseFloat(newTier.hma_percentage))
                      ? '—'
                      : (100 - parseFloat(newTier.hma_percentage)).toFixed(2)
                  }%.
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Create Tier'}
                  </button>
                  {tiers.length > 0 && (
                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {tiers.length > 0 && !showAddForm && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginBottom: 24 }}
              onClick={() => setShowAddForm(true)}
            >
              + Add Another Tier
            </button>
          )}

          {/* History */}
          {history.length > 0 && (
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Tier Change History</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Date', 'Tier', 'HMA %', 'Mentor %', 'Coach Keeps', 'Applied By', 'Notes'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px' }}>
                        {new Date(h.applied_at).toLocaleDateString('en-CA')}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{h.tier_name}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--accent)' }}>{h.hma_percentage}%</td>
                      <td style={{ padding: '10px 12px' }}>{h.mentor_percentage}%</td>
                      <td style={{ padding: '10px 12px', color: 'var(--success)', fontWeight: 600 }}>
                        {(100 - h.hma_percentage).toFixed(2)}%
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                        {h.applied_by_profile?.full_name || h.applied_by_profile?.email || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{h.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!selectedCoachId && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>◈</div>
          <div style={{ fontSize: 15 }}>Select a coach above to view and manage their split tiers.</div>
        </div>
      )}
    </div>
  );
}
